import { useFormContext } from "react-hook-form";
import { Input, cn } from "ui";
import { useField } from "../hooks/useField";
import { useFormFieldContext } from "./FormField";

interface InputFormFieldProps {
  type?: "text" | "number" | "email" | "password" | "hidden";
  min?: string;
  max?: string;
  icon?: React.ReactNode;
  placeholder?: string;
  autoComplete?: string;
  className?: string;
  encrypted?: boolean;
  copyable?: boolean;
  /**
   * Control value with string e.g. ENABLED || DISABLED
   */
  transform?: {
    value: (value: any) => string;
    onChange: (value: string) => any;
  };
}

export function InputFormField({
  type = "text",
  icon,
  placeholder,
  className,
  min,
  max,
  transform,
  autoComplete,
  encrypted,
  copyable,
}: InputFormFieldProps) {
  const {
    name,
    disabled,
    readOnly,
    required,
    defaultValue,
    validation,
    customValidation,
    descriptionId,
    errorMessageId,
    shouldUnregister,
  } = useFormFieldContext();

  const { ref, value, onChange, onBlur, error } = useField({
    name,
    defaultValue,
    validation,
    customValidation,
    shouldUnregister,
  });

  const { setValue } = useFormContext();

  return (
    <Input
      id={name}
      ref={ref}
      type={type}
      name={name}
      value={transform ? transform.value(value) : value || ""}
      onChange={(value) =>
        onChange(transform ? transform.onChange(value) : value)
      }
      onBlur={() => {
        if (!transform)
          setValue(name, String(value || "").trim(), {
            shouldValidate: true,
          });
        onBlur();
      }}
      className={cn(
        className,
        error &&
          "border-danger-bold focus:border-danger-bold focus:ring-danger-bold"
      )}
      placeholder={placeholder}
      disabled={disabled}
      autoComplete={autoComplete}
      readOnly={readOnly}
      required={required}
      aria-describedby={
        !error ? `${descriptionId}` : `${descriptionId} ${errorMessageId}`
      }
      aria-invalid={!!error}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (type === "number" && (e.key === "e" || e.key === "E")) {
          e.preventDefault();
        }
      }}
      icon={icon}
      min={min}
      max={max}
      encrypted={encrypted}
      copyable={copyable}
    />
  );
}
